import { useEffect, useState } from 'react'
import { AlertTriangle, Loader2, CheckCircle, AlertOctagon } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Checkbox } from '@/components/ui/checkbox'
import { api, getApiErrorMessage } from '@/lib/api'
import { toast } from 'sonner'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog'

interface AbuseFlag {
  id: number
  domain_id: string
  domain_name?: string
  flag_type: string
  severity: string
  status: string
  reason: string | null
  created_at: string
}

export function AbuseQueue() {
  const [flags, setFlags] = useState<AbuseFlag[]>([])
  const [loading, setLoading] = useState(true)
  const [actionLoading, setActionLoading] = useState<number | null>(null)
  const [resolveDialog, setResolveDialog] = useState<AbuseFlag | null>(null)
  const [restoreDomain, setRestoreDomain] = useState(true)

  const fetchFlags = async () => {
    setLoading(true)
    try {
      const res = await api.get('/deliverability/admin/abuse-queue')
      setFlags(res.data.flags || [])
    } catch {
      toast.error('Failed to load abuse queue')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchFlags()
  }, [])

  const handleResolve = async (id: number, restore: boolean) => {
    setActionLoading(id)
    try {
      await api.post(`/deliverability/admin/abuse-queue/${id}/resolve`, {
        resolution: 'Reviewed and resolved by admin',
        restore_domain: restore,
      })
      toast.success(restore ? 'Flag resolved and domain restored' : 'Flag resolved')
      fetchFlags()
    } catch (err: any) {
      toast.error(getApiErrorMessage(err, 'Failed to resolve'))
    } finally {
      setActionLoading(null)
      setResolveDialog(null)
    }
  }

  const openResolve = (flag: AbuseFlag) => {
    setResolveDialog(flag)
    setRestoreDomain(true)
  }

  const handleEscalate = async (id: number) => {
    setActionLoading(id)
    try {
      await api.post(`/deliverability/admin/abuse-queue/${id}/escalate`, {
        reason: 'Escalated for further review',
      })
      toast.success('Flag escalated')
      fetchFlags()
    } catch (err: any) {
      toast.error(getApiErrorMessage(err, 'Failed to escalate'))
    } finally {
      setActionLoading(null)
    }
  }

  const severityColor = (severity: string) => {
    switch (severity) {
      case 'critical': return 'bg-red-500/10 text-red-500'
      case 'high': return 'bg-orange-500/10 text-orange-500'
      case 'medium': return 'bg-amber-500/10 text-amber-500'
      default: return 'bg-blue-500/10 text-blue-500'
    }
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
          <AlertTriangle className="h-5 w-5 text-primary" />
          Abuse Queue
        </h1>
        <p className="text-sm text-muted-foreground mt-1">Review and manage abuse flags</p>
      </div>

      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-muted/50">
                  <th className="px-4 py-3 text-left font-medium">Domain</th>
                  <th className="px-4 py-3 text-left font-medium">Type</th>
                  <th className="px-4 py-3 text-left font-medium">Severity</th>
                  <th className="px-4 py-3 text-left font-medium">Status</th>
                  <th className="px-4 py-3 text-left font-medium">Created</th>
                  <th className="px-4 py-3 text-right font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={6} className="px-4 py-8 text-center">
                      <Loader2 className="h-5 w-5 animate-spin mx-auto" />
                    </td>
                  </tr>
                ) : flags.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-4 py-8 text-center text-muted-foreground">
                      No abuse flags in queue
                    </td>
                  </tr>
                ) : (
                  flags.map((flag) => (
                    <tr key={flag.id} className="border-b hover:bg-muted/30 transition-colors">
                      <td className="px-4 py-3 font-medium">
                        {flag.domain_name || flag.domain_id.slice(0, 8) + '...'}
                      </td>
                      <td className="px-4 py-3">{flag.flag_type}</td>
                      <td className="px-4 py-3">
                        <Badge variant="secondary" className={severityColor(flag.severity)}>
                          {flag.severity}
                        </Badge>
                      </td>
                      <td className="px-4 py-3">
                        <Badge variant={flag.status === 'resolved' ? 'default' : 'destructive'}>
                          {flag.status}
                        </Badge>
                      </td>
                      <td className="px-4 py-3 text-muted-foreground">
                        {new Date(flag.created_at).toLocaleDateString()}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex items-center justify-end gap-1">
                          {flag.status !== 'resolved' && (
                            <>
                              <Button
                                variant="ghost"
                                size="icon"
                                title="Resolve"
                                onClick={() => openResolve(flag)}
                                disabled={actionLoading === flag.id}
                              >
                                {actionLoading === flag.id ? (
                                  <Loader2 className="h-4 w-4 animate-spin" />
                                ) : (
                                  <CheckCircle className="h-4 w-4 text-emerald-500" />
                                )}
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                title="Escalate"
                                onClick={() => handleEscalate(flag.id)}
                                disabled={actionLoading === flag.id}
                              >
                                <AlertOctagon className="h-4 w-4 text-red-500" />
                              </Button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      <Dialog open={!!resolveDialog} onOpenChange={(open) => !open && setResolveDialog(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Resolve Abuse Flag</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <p className="text-sm text-muted-foreground">
              Resolve flag for <strong>{resolveDialog?.domain_name || resolveDialog?.domain_id}</strong>?
            </p>
            <div className="flex items-start gap-3">
              <Checkbox
                id="restore-domain"
                checked={restoreDomain}
                onCheckedChange={(checked) => setRestoreDomain(checked === true)}
              />
              <label htmlFor="restore-domain" className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                Restore associated domain to active
              </label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setResolveDialog(null)}>Cancel</Button>
            <Button
              onClick={() => resolveDialog && handleResolve(resolveDialog.id, restoreDomain)}
              disabled={actionLoading === resolveDialog?.id}
            >
              {actionLoading === resolveDialog?.id ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Resolve
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
